/**
 * @name AccountIconButton
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Botão para navegar para a página "Minha Conta" com animações suaves
 * @param {AccountIconButtonProps} props - As props para o componente.
 * @returns {JSX.Element} - O JSX do componente.
 * @package src/components/common/iconButtons/AccountIconButton.tsx
 **/

import PersonIcon from '@mui/icons-material/Person';
import { Box, IconButton, Tooltip } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface AccountIconButtonProps {
     color?: 'inherit' | 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
     size?: 'small' | 'medium' | 'large';
     edge?: 'start' | 'end' | false;
     onClick?: () => void;
}

const AccountIconButton: React.FC<AccountIconButtonProps> = ({ color = 'default', size = 'medium', edge = false, onClick }) => {
     const navigate = useNavigate();
     const [isAnimating, setIsAnimating] = useState(false);

     const handleClick = () => {
          setIsAnimating(true);
          setTimeout(() => {
               navigate('/minha-conta');
               onClick?.(); // Chama a função onClick se fornecida (para fechar menu, etc.)
               setTimeout(() => setIsAnimating(false), 300);
          }, 150);
     };

     return (
          <Tooltip
               title='Minha Conta'
               arrow
               placement='top'
          >
               <Box
                    sx={{
                         position: 'relative',
                         display: 'inline-flex',
                         borderRadius: '50%',
                         overflow: 'hidden',
                    }}
               >
                    <IconButton
                         color={color}
                         size={size}
                         edge={edge}
                         onClick={handleClick}
                         disabled={isAnimating}
                         sx={{
                              position: 'relative',
                              background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 50%, #42a5f5 100%)',
                              color: '#ffffff',
                              borderRadius: '50%',
                              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                              transform: isAnimating ? 'scale(1.1) rotate(180deg)' : 'scale(1) rotate(0deg)',
                              boxShadow: '0 4px 20px rgba(25, 118, 210, 0.4), 0 0 0 2px rgba(33, 150, 243, 0.2)',
                              '&:hover': {
                                   transform: isAnimating ? 'scale(1.1) rotate(180deg)' : 'scale(1.05) rotate(5deg)',
                                   boxShadow: '0 6px 25px rgba(25, 118, 210, 0.6), 0 0 0 3px rgba(33, 150, 243, 0.3)',
                              },
                              '&:active': {
                                   transform: 'scale(0.95)',
                              },
                              '&::before': {
                                   content: '""',
                                   position: 'absolute',
                                   top: 0,
                                   left: 0,
                                   right: 0,
                                   bottom: 0,
                                   background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.3) 0%, transparent 50%)',
                                   borderRadius: '50%',
                                   opacity: 1,
                                   transition: 'opacity 0.3s ease',
                              },
                              '&:hover::before': {
                                   opacity: 0.8,
                              },
                         }}
                    >
                         <Box
                              sx={{
                                   position: 'relative',
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center',
                                   transform: isAnimating ? 'rotate(-180deg) scale(1.2)' : 'rotate(0deg) scale(1)',
                                   transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                   zIndex: 1,
                              }}
                         >
                              <PersonIcon
                                   sx={{
                                        fontSize: size === 'small' ? '1.2rem' : size === 'large' ? '1.8rem' : '1.5rem',
                                        filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.3))',
                                   }}
                              />
                         </Box>
                    </IconButton>
               </Box>
          </Tooltip>
     );
};

export default AccountIconButton;
